import { useMemo } from "react"
import { Query } from "@cubejs-client/core"
import dayjs from "dayjs"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"
import { type TColumnFiltersState } from "~/ui-rtk/constants/table-visualization-types"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"

// Every member below is a member of the `mega_feed` Cube view
// (cube/model/views/mega_feed.js), which joins the Shopify product feed to GA4
// per-product sales performance on the Shopify product id.
export const CUBE_PREFIX = "mega_feed"

export const ITEMS_PER_PAGE = 25

// ── Product attributes (Cube dimensions) ─────────────────────────────────────
//
// These are the feed side of the view. They are dimensions, not measures, so
// every one added here widens the GROUP BY — keep this list to fields that are
// genuinely useful in a table. The long-text fields (description,
// product_detail_description, specs) and the image ARRAY fields are
// deliberately excluded: they are available on the view for feed export, but
// they would make the grid unreadable and blow up the group-by.
export const MEGA_FEED_ATTRIBUTES_MAP = {
  image_link: "Image",
  parent_title: "Product",
  item_group_id: "Product ID",
  handle: "Handle",
  id_sku: "SKU",
  // First N characters of the SKU (MD-5536): merchants encode brand /
  // category / subcategory left to right, so a prefix groups products.
  sku_prefix_1: "SKU First 1",
  sku_prefix_2: "SKU First 2",
  sku_prefix_3: "SKU First 3",
  sku_prefix_4: "SKU First 4",
  sku_prefix_5: "SKU First 5",
  sku_prefix_6: "SKU First 6",
  sku_prefix_7: "SKU First 7",
  sku_prefix_8: "SKU First 8",
  sku_prefix_9: "SKU First 9",
  sku_prefix_10: "SKU First 10",
  sku_prefix_11: "SKU First 11",
  sku_prefix_12: "SKU First 12",
  brand: "Brand",
  product_type: "Product Type",
  google_product_category: "Google Category",
  collections: "Collections",
  tags: "Tags",
  availability: "In Stock",
  price: "Price",
  sale_price: "Sale Price",
  quantity: "Inventory",
  total_variants: "Variants",
  image_count: "Images",
  condition: "Condition",
  age_group: "Age Group",
  gender: "Gender",
  shipping_country: "Ships From",
  gtin: "GTIN",
  link: "URL",
}

export type TAttributeKey = keyof typeof MEGA_FEED_ATTRIBUTES_MAP

// Always present, in this order. `image_link` and `parent_title` identify the
// row; item_group_id is the join key and the stable id for drill-through.
export const ALWAYS_ATTRIBUTES: TAttributeKey[] = [
  "image_link",
  "parent_title",
  "item_group_id",
]

export const DEFAULT_ATTRIBUTES: TAttributeKey[] = [
  "brand",
  "product_type",
  "availability",
  "price",
]

export const SKU_PREFIX_ATTRIBUTES = (
  Object.keys(MEGA_FEED_ATTRIBUTES_MAP) as TAttributeKey[]
).filter(attribute => attribute.startsWith("sku_prefix_"))

export const IMAGE_ATTRIBUTES: TAttributeKey[] = ["image_link"]
export const BOOLEAN_ATTRIBUTES: TAttributeKey[] = ["availability"]
export const MONETARY_ATTRIBUTES: TAttributeKey[] = ["price", "sale_price"]
export const NUMERIC_ATTRIBUTES: TAttributeKey[] = [
  "quantity",
  "total_variants",
  "image_count",
]

// ── Sales metrics (Cube measures) ────────────────────────────────────────────
//
// All of these are bounded by the page's date range via the md_date time
// dimension. The view also exposes self-bounding *_last_7d / *_last_30d
// variants; those are intentionally NOT surfaced here, because Cube would AND
// the page's dateRange onto their own window and silently return the
// intersection. Use the date-range presets instead.
export const MEGA_FEED_METRICS_MAP = {
  // Revenue.
  //
  // total_revenue / revenue_per_session come from the GA4 LANDING-PAGE report:
  // every purchase in a session is credited to the page that session entered on.
  // They answer "what does traffic to this product page produce", not "what did
  // this product sell" — Shopify Revenue (the merged column) is the order-derived
  // figure for the latter. The labels stay plain by request; the distinction lives
  // here and in the view.
  total_revenue: "Revenue",
  sum_first_order_revs: "1st Order Revenue",
  sum_second_order_revs: "2nd Order Revenue",
  sum_first_order_line_revenue: "1st Order Line Revenue",
  sum_first_order_ltv_180_day: "1st Order LTV 180d",
  sum_first_order_pre_mktg_contribution_180_day: "1st Order Pre-Mktg 180d",
  expected_new_customer_session_ltv: "Expected New Customer Session LTV",

  // Orders
  total_orders: "Total Orders",
  first_orders: "First Orders",
  repeat_orders: "Repeat Orders",
  first_order_aov: "First Order AOV",

  // Traffic
  sessions: "Sessions",
  screen_page_views: "Page Views",
  add_to_carts: "Add to Carts",
  checkouts: "Checkouts",
  bounces: "Bounces",

  // Efficiency
  revenue_per_session: "Revenue per Session",
  conversion_rate: "Conversion Rate",
  add_to_cart_rate: "Add to Cart Rate",
  checkout_rate: "Checkout Rate",
  bounce_rate: "Bounce Rate",
}

export type TMetricsMapKey = keyof typeof MEGA_FEED_METRICS_MAP

export const ALL_METRICS = Object.keys(
  MEGA_FEED_METRICS_MAP,
) as TMetricsMapKey[]

export const REVENUE_METRICS: TMetricsMapKey[] = [
  "total_revenue",
  "sum_first_order_revs",
  "sum_second_order_revs",
  "sum_first_order_line_revenue",
  "sum_first_order_ltv_180_day",
  "sum_first_order_pre_mktg_contribution_180_day",
  "expected_new_customer_session_ltv",
]

export const ORDER_METRICS: TMetricsMapKey[] = [
  "total_orders",
  "first_orders",
  "repeat_orders",
  "first_order_aov",
]

export const TRAFFIC_METRICS: TMetricsMapKey[] = [
  "sessions",
  "screen_page_views",
  "add_to_carts",
  "checkouts",
  "bounces",
]

export const EFFICIENCY_METRICS: TMetricsMapKey[] = [
  "revenue_per_session",
  "conversion_rate",
  "add_to_cart_rate",
  "checkout_rate",
  "bounce_rate",
]

// Always requested so the table is never metric-less and the default sort has
// something to sort on.
//
// Kept as first-order revenue even though total_revenue is now populated: it is
// order-derived and product-attributed, so ranking by it answers "which products
// sell", whereas ranking by the landing-page figure answers "which pages catch
// traffic". The latter is a column, not the default ordering.
export const ALWAYS_METRICS: TMetricsMapKey[] = ["sum_first_order_revs"]

export const DEFAULT_METRICS: TMetricsMapKey[] = [
  "total_revenue",
  "sessions",
  "total_orders",
  "conversion_rate",
  "first_order_aov",
]

export const MONETARY_METRICS: TMetricsMapKey[] = [
  "total_revenue",
  "sum_first_order_revs",
  "sum_second_order_revs",
  "sum_first_order_line_revenue",
  "sum_first_order_ltv_180_day",
  "sum_first_order_pre_mktg_contribution_180_day",
  "expected_new_customer_session_ltv",
  "first_order_aov",
  "revenue_per_session",
]

export const PERCENTAGE_METRICS: TMetricsMapKey[] = [
  "conversion_rate",
  "add_to_cart_rate",
  "checkout_rate",
  "bounce_rate",
]

// ── Query builder ────────────────────────────────────────────────────────────

export default function useConnect(
  attributes: TAttributeKey[],
  metrics: TMetricsMapKey[],
  dateRange: [Date, Date],
  page: number,
  columnFilters: TColumnFiltersState | undefined,
  sorting: TSortingState | undefined,
) {
  const cubeFilters = useCubeFiltersFromColumnFilters(
    CUBE_PREFIX,
    columnFilters,
  )

  const cubeQuery = useMemo(() => {
    // ALWAYS_ATTRIBUTES first so the identifying columns lead the table, then
    // whatever the user picked, de-duplicated.
    //
    // `handle` is always selected even when it isn't a visible column: it is the
    // key the page uses to merge in paid-media metrics from the separate
    // ads-by-product query (see hooks/useAdMetrics).
    const dimensions = Array.from(
      new Set([...ALWAYS_ATTRIBUTES, ...attributes, "handle"]),
    ).map(attribute => `${CUBE_PREFIX}.${attribute}`)

    const measures = Array.from(new Set([...ALWAYS_METRICS, ...metrics])).map(
      metric => `${CUBE_PREFIX}.${metric}`,
    )

    const q = {
      dimensions,
      measures,
      offset: page * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
      total: true,
    } as Query

    if (sorting) {
      q.order = [
        [`${CUBE_PREFIX}.${sorting.id}`, sorting.desc ? "desc" : "asc"],
      ]
    }

    // Whole days, matching the creative report. md_date is the view's only time
    // dimension and it bounds every measure requested above.
    const wholeDays: [Date, Date] = [
      dayjs(dateRange[0]).startOf("day").toDate(),
      dayjs(dateRange[1]).endOf("day").toDate(),
    ]

    const query = polyfillCubeQueryWithTimeDimension(
      {
        ...q,
        timeDimensions: [{ dimension: `${CUBE_PREFIX}.md_date` }],
      },
      { dateRange: wholeDays },
    )

    if (cubeFilters.length > 0) {
      query.filters = [...(query.filters ?? []), ...cubeFilters]
    }

    return query
  }, [attributes, metrics, dateRange, page, cubeFilters, sorting])

  return { cubeQuery, prefix: CUBE_PREFIX }
}
